import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button } from '../../components/Button';
import { CheckBox } from '../../components/CheckBox';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { Title } from '../../components/Title';
import { SLoginStatus } from '../../specific';
import { SLoginStatusProps } from '../../specific/SLoginStatus';
import MouraLogo from '../../../assets/svg-moura-logosingle.svg';

const IS_LOCAL = import.meta.env.VITE_IS_LOCAL;
const STATIC_BASE_URL = import.meta.env.VITE_STATIC_URI;

const Content = styled.div`
  display: grid;

  width: 100vw;
  height: 100vh;
  justify-content: center;
  justify-items: center;
  align-items: center;
  overflow-x: hidden;
  overflow-y: hidden;
  grid-template:
    'Login Image' 1fr /
    1fr 1fr;

  @media (max-width: 1150px) {
    grid-template:
      'Login ' 1fr /
      1fr;
  }
`;

const BodyStyle = styled.div`
  display: grid;
  grid-area: Image;
  background-image: url('${STATIC_BASE_URL}/bg-bess.png');
  background-attachment: fixed;
  background-repeat: no-repeat;
  background-size: auto;
  background-position: right;
  width: 1350px;
  height: 100%;

  @media screen and (min-width: 1920px) {
    background-size: cover;
    width: 1600px;
  }

  @media screen and (min-width: 2500px) {
    background-size: cover;
    width: 1800px;
  }

  @media screen and (min-width: 3000px) {
    background-size: cover;
    width: 2500px;
  }

  @media (max-width: 1780px) {
    width: 1200px;
  }
  @media (max-width: 1580px) {
    width: 1000px;
  }
  @media (max-width: 1380px) {
    width: 800px;
  }
  @media (max-width: 1150px) {
    width: 0px;
    height: 0px;
  }
`;
/* background-repeat: no-repeat;
  background-position: right; */

/**
 * Template layout of this page for each screen size supported
 */
const StyledContainer = styled.div`
  display: grid;
  grid-area: Login;
  padding: 20px;
  gap: 20px;
  grid-template-columns: 133px 133px;
  grid-auto-rows: max-content;
  justify-content: center;
  justify-items: center;
  align-items: center;
`;

export type ViewLoginProps = {
  /**
   * Called once the user hits the button to login.
   */
  onLogin: (email: string, password: string, remember: boolean) => void;
  /**
   * Called once the user hits the button signaling that the password was
   * lost.
   */
  onRecoverPassword: (mail: string) => void;
  /**
   * Current connection status.
   */
  status?: SLoginStatusProps['status'];
  /**
   * If the user marks the remember option, the email goes here.
   */
  defaultEmail?: string;
};

/**
 * Sensing Complete View
 */
export const ViewLogin: React.FC<ViewLoginProps> = ({
  status,
  onLogin,
  onRecoverPassword,
  defaultEmail,
}: ViewLoginProps) => {
  const [remember, setRemember] = useState(false);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const searchParams = new URLSearchParams(window.location.search);
  const urlError = searchParams.get('error');
  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail);
  }, [defaultEmail]);

  return (
    <>
      <Content>
        <BodyStyle />

        <StyledContainer>
          <Logo width='600' height='300' pad='none' area='1 / 1 / span 2 / span 2' />
          <Input
            area='3 / 1 / auto / span 2'
            type='email'
            value={email}
            onChange={(element) => setEmail(element.target.value)}
            placeholder='Seu email'
            radius='medium'
            size='large'
            icon
            colorBackground='input'
            colorText='white'
            border='1px solid transparent'
          />
          <Input
            area='4 / 1 / auto / span 2'
            type='password'
            value={password}
            onChange={(element) => setPassword(element.target.value)}
            onPressEnter={() => onLogin(email, password, remember)}
            placeholder='Sua senha'
            radius='medium'
            size='large'
            colorBackground='input'
            colorText='white'
            border='1px solid transparent'
          />
          <Button
            area='6 / 1 / auto / span 2'
            onClick={() => onLogin(email, password, remember)}
            color='secondary'
            size='auto'>
            <Title size='small' color='black' value='Entrar' />
          </Button>
          <Button
            area='7 / 1 / auto / span 2'
            onClick={() => {
              const baseUrl = import.meta.env.VITE_API_URI?.startsWith('http')
                ? import.meta.env.VITE_API_URI
                : `http://${import.meta.env.VITE_API_URI}:4005`;

              window.location.href = `${baseUrl}/auth/signinFederated`;
            }}
            color='light'
            size='auto'
            valign='center'
            salign={{ horizontal: 'center', vertical: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <img src={MouraLogo} alt='Moura Logo' style={{ width: '30px', height: 'auto' }} />
              <Title size='small' color='white' value='Acesso Moura' />
            </div>
          </Button>
          {import.meta.env.VITE_IS_LOCAL == 'true' && import.meta.env.VITE_STATIC_STATION == 'true' ? (
            <Button
              area='7 / 1 / auto / span 2'
              onClick={() =>
                onLogin(
                  import.meta.env.VITE_EMAIL_CLIENT_STATIC ?? '',
                  import.meta.env.VITE_PASSWORD_CLIENTE_STATIC ?? '',
                  false
                )
              }
              color='success'
              size='auto'>
              <Title size='small' color='white' value='Eletroposto' />
            </Button>
          ) : null}
          <CheckBox
            area='5 / 1'
            id='check'
            label='Lembrar email'
            size='small'
            color='white'
            halign='start'
            padAll={{ right: 'large' }}
            checked={remember}
            onChange={(evt) => setRemember(evt.target.checked)}
          />
          {IS_LOCAL == 'false' && (
            <Title
              area='5 / 2'
              size='xsmall'
              color='white'
              valign='center'
              halign='center'
              value='Esqueci minha senha'
              onClick={() => onRecoverPassword(email)}
            />
          )}

          {urlError && (
            <SLoginStatus
              padCross={{ horizontal: 'small', vertical: 'none' }}
              area='8 / 1 / auto / span 2'
              status={'error'}
              size='auto'
            />
          )}

          {status && (
            <SLoginStatus
              padCross={{ horizontal: 'small', vertical: 'none' }}
              area='8 / 1 / auto / span 2'
              status={status}
              size='auto'
            />
          )}
        </StyledContainer>
      </Content>
    </>
  );
};
